import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Icon from './AppIcon';
import { useUserAccess } from '../context/UserAccessContext';
import { isStandalonePublicRoute } from '../access/publicRoutes';
import {
  getAvailableApps,
  getDefaultCrmPath,
  getDefaultEnterprisePath,
  getDefaultWebCreatorPath,
} from '../access/userAccess';
import JustOnePageLogo from '../pages/web-creator/components/JustOnePageLogo';

const APP_META = {
  crm: {
    icon: 'BarChart3',
    title: 'Accès CRM',
    description: 'CRM Booster — pipelines, prospects, campagnes',
  },
  hubup: {
    icon: 'Sparkles',
    title: 'Accès Hubup',
    description: 'Tableau de bord, rubriques et notifications',
  },
  'web-creator': {
    icon: 'LayoutTemplate',
    useLogo: true,
    title: 'Just One Page',
    description: 'Créez et publiez vos landing pages',
  },
  enterprise: {
    icon: 'Building2',
    title: 'Entreprises',
    description: 'Équipe, rôles et réglages',
  },
};

const AppChooserModal = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { chooserOpen, user, chooseAppMode } = useUserAccess();

  if (isStandalonePublicRoute(location.pathname) || !chooserOpen) return null;

  const apps = getAvailableApps(user);

  const go = (mode) => {
    chooseAppMode(mode);
    if (mode === 'crm') navigate(getDefaultCrmPath(user), { replace: true });
    else if (mode === 'hubup') navigate('/hubup', { replace: true });
    else if (mode === 'enterprise') navigate(getDefaultEnterprisePath(), { replace: true });
    else navigate(getDefaultWebCreatorPath(), { replace: true });
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="app-chooser-title"
    >
      <div className="w-full max-w-2xl rounded-2xl bg-card border border-border shadow-elevation-3 p-6 md:p-8 space-y-6">
        <div className="text-center space-y-2">
          <div className="mx-auto w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
            <Icon name="LayoutGrid" size={24} className="text-primary" />
          </div>
          <h2 id="app-chooser-title" className="text-xl font-semibold text-foreground">
            Choisir votre espace
          </h2>
          <p className="text-sm text-muted-foreground">
            Sélectionnez l&apos;application à laquelle vous souhaitez accéder.
          </p>
        </div>
        <div
          className={`grid grid-cols-1 gap-3 ${
            apps.length >= 4
              ? 'sm:grid-cols-2 lg:grid-cols-2'
              : apps.length > 2
                ? 'sm:grid-cols-3'
                : 'sm:grid-cols-2'
          }`}
        >
          {apps.map((mode) => {
            const meta = APP_META[mode];
            return (
              <button
                key={mode}
                type="button"
                onClick={() => go(mode)}
                className="flex flex-col items-start gap-2 rounded-xl border border-border bg-background p-4 text-left hover:bg-muted/60 transition-smooth focus:outline-none focus:ring-2 focus:ring-ring"
              >
                {meta.useLogo ? (
                  <JustOnePageLogo size="sm" className="mb-1" />
                ) : (
                  <Icon name={meta.icon} size={22} className="text-primary" />
                )}
                <span className="font-medium text-foreground">{meta.title}</span>
                <span className="text-xs text-muted-foreground">{meta.description}</span>
              </button>
            );
          })}
        </div>
        <p className="text-[11px] text-center text-muted-foreground">
          Vous pourrez changer d&apos;espace plus tard depuis le menu utilisateur.
        </p>
      </div>
    </div>
  );
};

export default AppChooserModal;
